
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, FileDown, AlertTriangle } from "lucide-react";

// Mock data
const expiryData = [
  {
    id: 1,
    name: "Fresh Milk 1L",
    batch: "FM2305A",
    expiryDate: "2025-05-30",
    quantity: 18,
    status: "warning" // warning, critical, ok
  },
  {
    id: 2,
    name: "Yogurt 500g",
    batch: "YG2305B",
    expiryDate: "2025-05-25",
    quantity: 12,
    status: "critical"
  },
  {
    id: 3,
    name: "Chicken Breast (Fresh)",
    batch: "CB2305C",
    expiryDate: "2025-05-20",
    quantity: 8,
    status: "critical"
  },
  {
    id: 4,
    name: "Organic Eggs (Dozen)",
    batch: "OE2304D",
    expiryDate: "2025-06-15",
    quantity: 15,
    status: "warning"
  },
  {
    id: 5,
    name: "Cereal Bars (Box)",
    batch: "CB2301E",
    expiryDate: "2025-11-30",
    quantity: 25,
    status: "ok"
  }
];

const ExpiryTrackerTab = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expiryThreshold, setExpiryThreshold] = useState("30");
  
  const filteredExpiryData = expiryData.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.batch.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const calculateDaysRemaining = (expiryDate: string) => {
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  const getStatusClass = (status: string) => {
    switch (status) {
      case "critical":
        return "bg-red-100 text-red-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "";
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Expiry Tracker</h2>
        <div className="flex gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Expiry Warning:</span>
            <select 
              className="rounded border px-2 py-1 text-sm" 
              value={expiryThreshold}
              onChange={(e) => setExpiryThreshold(e.target.value)}
            >
              <option value="30">30 Days</option>
              <option value="60">60 Days</option>
              <option value="90">90 Days</option>
            </select>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search products..."
              className="pl-8 w-[250px]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button variant="outline">
            <FileDown className="mr-2 h-4 w-4" />
            Export Report
          </Button>
        </div>
      </div>
      
      <div className="border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product Name</TableHead>
              <TableHead>Batch Number</TableHead>
              <TableHead>Expiry Date</TableHead>
              <TableHead className="text-center">Days Remaining</TableHead>
              <TableHead className="text-center">Quantity</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredExpiryData.map((item) => {
              const daysRemaining = calculateDaysRemaining(item.expiryDate);
              return (
                <TableRow key={item.id} className={getStatusClass(item.status)}>
                  <TableCell className="font-medium">
                    {item.name}
                    {item.status === "critical" && (
                      <AlertTriangle className="inline ml-2 h-4 w-4 text-red-600" />
                    )}
                  </TableCell>
                  <TableCell>{item.batch}</TableCell>
                  <TableCell>{new Date(item.expiryDate).toLocaleDateString()}</TableCell>
                  <TableCell className="text-center">
                    <span className={daysRemaining < 15 ? "text-red-600 font-medium" : ""}>
                      {daysRemaining}
                    </span>
                  </TableCell>
                  <TableCell className="text-center">{item.quantity}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="outline">Mark Down</Button>
                    <Button size="sm" variant="outline">Remove</Button>
                    <Button size="sm">Return</Button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ExpiryTrackerTab;
